import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ticketService } from "../services/ticketService";
import {
  setIssueFilterValues,
  clearIssueFilterValues,
  setIssueFilterOptions,
} from "../../../../redux/slices/issueFilters";

export default function useData(isListView = false) {
  // Add isListView parameter
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Get filter metadata (options) and applied values from Redux.
  // The options are populated by the Navbar API call.
  const { options: rawFilters = [], values: reduxFilterData = {} } =
    useSelector((state) => state.issueFilters || {});

  // Transform metadata to extract dataset arrays for the UI components.
  const filters = useMemo(() => {
    if (!rawFilters || !Array.isArray(rawFilters)) return [];
    return rawFilters.map((f) => ({
      ...f,
      dataset:
        f.type === "list" && f.dataset?.dataset ? f.dataset.dataset : f.dataset,
    }));
  }, [rawFilters]);

  // List & Chart State
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [doughnutChartData, setDoughnutChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);

  // Detail State
  const [ticket, setTicket] = useState(null);

  // Filter State
  const [filtersActive, setFiltersActive] = useState(true);

  const initialFilterData = useMemo(
    () => ({
      oggetto: "",
      tipo: "",
      categoria: "",
      stato: "",
      priorita: "",
    }),
    [],
  );

  // Local temporary state for the input fields
  const [filterData, setFilterData] = useState(
    reduxFilterData || initialFilterData,
  );

  // appliedFilters is what determines the actual data fetching
  const appliedFilters = reduxFilterData || initialFilterData;

  // Sync local inputs when Redux state changes (e.g., cleared from another component)
  useEffect(() => {
    setFilterData(reduxFilterData || initialFilterData);
  }, [reduxFilterData, initialFilterData]);

  const [filtersLoading, setFiltersLoading] = useState(false);
  const [isRemovingFilters, setIsRemovingFilters] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ticketService.getList(
        pageSize,
        pageNumber,
        appliedFilters,
        navigate,
      );
      if (response.status === 1) {
        setRows(response.data?.dataset || []);
        setTotalRows(response.data?.totalRows || 0);
        setDoughnutChartData(response.data.graphCircleData);
        setBarChartData(response.data.graphBarData);
      }
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoading(false);
      setFiltersLoading(false);
      setIsRemovingFilters(false);
    }
  }, [pageSize, pageNumber, appliedFilters]);

  useEffect(() => {
    // Only fetch tickets if it's a list view
    if (isListView) {
      fetchTickets();
    }
  }, [fetchTickets, isListView]); // Add isListView to dependencies

  const handleApplyFilters = () => {
    setFiltersLoading(true);
    dispatch(setIssueFilterValues(filterData));
    setPageNumber(1);
  };

  const handleRemoveFilters = () => {
    setIsRemovingFilters(true);
    dispatch(clearIssueFilterValues());
    setPageNumber(1);
  };

  const switchFilterActive = () => setFiltersActive((prev) => !prev);

  const isAnyFieldFilled = Object.values(filterData).some(
    (value) => value !== "" && value !== "all",
  );

  const canClearFilters = Object.values(appliedFilters).some(
    (value) => value !== "" && value !== "all",
  );

  const fetchTicketDetails = useCallback(
    async (id, navigate) => {
      if (!id) return;
      try {
        const t = await ticketService.getDetails(id, navigate);
        if (t) setTicket(t.data[0] || t);
      } catch (error) {
        console.error("Failed to fetch ticket details:", error);
      }
    },
    [navigate],
  );

  return {
    rows,
    totalRows,
    pageSize,
    setPageSize,
    pageNumber,
    setPageNumber,
    loading,
    doughnutChartData,
    barChartData,
    filters,
    filtersActive,
    switchFilterActive,
    filterData,
    setFilterData,
    handleApplyFilters,
    handleRemoveFilters,
    filtersLoading,
    isRemovingFilters,
    isAnyFieldFilled,
    canClearFilters,
    ticket,
    setTicket,
    fetchTicketDetails,
  };
}
